/**
 * 浮岛(方案 C3):主岛(倒锥体+岩须+两级台地+摇曳树+rim光边)+ 远景视差岛。
 * 全部剪影色/rim 派生自 SkyState;每帧重绘(简单几何,60fps 内)。
 */
import { Container, Graphics } from 'pixi.js';
import { Stage, LW, LH, GROUND_Y } from '../engine/Stage';
import { sky } from '../engine/SkyRenderer';
import { mix, darken } from '../engine/Palette';
import { world as windSrc } from '../engine/Ticker';
import { rand } from '../engine/util';

const CX = LW * 0.5;
const IW = 1150; // 主岛宽
const HALF = IW / 2;

interface Tree { x: number; baseY: number; h: number; ph: number; crowns: { dx: number; dy: number; r: number }[]; }

export class Island {
  private body = new Graphics();
  private trees = new Graphics();
  private far: Graphics;
  private treeList: Tree[] = [];
  private t = 0;
  private parX = 0;
  /** 树木生长度 0~1。混沌/黎明纪无树(树属于"生命"),生命纪起缓缓长出。
   *  默认 1:dev.html 与未接入纪元逻辑的场景保持原样。 */
  private treeGrow = 1;
  private treeGrowTarget = 1;
  /** 雨湿度 0~1:雨/暴雨时岛面剪影渐深(雨"改变"世界而非叠加于世界)。 */
  private wet = 0;

  constructor(private stage: Stage) {
    stage.layers.mainIsland.addChildAt(this.body, 0);
    stage.layers.mainIsland.addChild(this.trees);
    this.far = new Graphics();
    stage.layers.islandFar.addChild(this.far);
    // 4 棵树(避开中央火种)
    for (const x of [CX - 330, CX - 190, CX + 200, CX + 350]) {
      const h = rand(46, 62);
      this.treeList.push({
        x, baseY: this.surfaceYAt(x), h, ph: rand(0, 6.28),
        crowns: [
          { dx: 0, dy: -h, r: rand(24, 30) },
          { dx: -16, dy: -h + 10, r: rand(18, 24) },
          { dx: 16, dy: -h + 8, r: rand(18, 24) },
        ],
      });
    }
  }

  /** 岛面海拔线在 x 处的 y(供雨/雪/粒子落点) */
  surfaceYAt(x: number): number {
    const dx = (x - CX) / HALF;
    if (Math.abs(dx) > 1) return LH * 2;
    return GROUND_Y - Math.cos((dx * Math.PI) / 2) * 26;
  }

  /** 纪元驱动树木生长。instant 用于初始化(避免开局播放生长动画)。 */
  setTreesGrown(target: number, instant = false) {
    this.treeGrowTarget = target;
    if (instant) this.treeGrow = target;
  }

  update(dt: number) {
    this.t += dt;
    // 树木生长(6s 缓长)与雨湿度(慢进慢出)
    this.treeGrow += (this.treeGrowTarget - this.treeGrow) * Math.min(1, dt / 3);
    const raining = sky.weather === 'rain' || sky.weather === 'storm';
    this.wet += ((raining ? 1 : 0) - this.wet) * Math.min(1, dt * (raining ? 0.12 : 0.045));
    const sil = this.wet > 0.01 ? darken(sky.silhouette, this.wet * 0.14) : sky.silhouette;
    const rim = sky.rimLight;

    // 远景视差(随风缓移)
    this.parX = windSrc.wind.x * 0.04;
    this.far.x = this.parX;
    this.drawFar(sil);

    // 主岛体
    this.drawBody(sil, rim);
    // 树(摇曳)
    this.drawTrees(sil);
  }

  private drawFar(sil: number) {
    const g = this.far; g.clear();
    const c1 = mix(sil, sky.state.bottom, 0.55);
    const c2 = mix(sil, sky.state.bottom, 0.3);
    // L1 两座小岛
    this.coneAt(g, LW * 0.16, GROUND_Y - 40, 160, c2, 0.55);
    this.coneAt(g, LW * 0.86, GROUND_Y - 30, 130, c2, 0.55);
    // L2 一座中岛
    this.coneAt(g, LW * 0.72, GROUND_Y + 10, 240, c1, 0.75);
  }

  private coneAt(g: Graphics, x: number, y: number, w: number, color: number, alpha: number) {
    g.moveTo(x - w / 2, y);
    g.bezierCurveTo(x - w * 0.2, y - w * 0.08, x + w * 0.2, y - w * 0.08, x + w / 2, y);
    g.lineTo(x + w * 0.1, y + w * 0.5);
    g.lineTo(x, y + w * 0.7);
    g.lineTo(x - w * 0.1, y + w * 0.5);
    g.closePath();
    // 微型树影×3:一根 1px 干 + 一粒小冠。纯几何三角远岛读作"图形",
    // 有了尺度参照物才读作"远方的岛"(空气透视的说服力来自细节密度差)。
    for (const [dx, hs] of [[-w * 0.22, 0.055], [w * 0.05, 0.07], [w * 0.3, 0.05]] as const) {
      const tx = x + dx, th = w * hs;
      g.rect(tx - 0.6, y - w * 0.055 - th, 1.2, th);
      g.circle(tx, y - w * 0.055 - th, th * 0.55);
    }
    g.fill({ color, alpha });
  }

  /** 岛体轮廓路径(§B3:圆润剪影,一律贝塞尔,禁止直角锯齿)。fill 与 stroke 各描一次,不复用路径。 */
  private tracedTop(g: Graphics): [number, number][] {
    const top: [number, number][] = [];
    const bumps = 8;
    for (let i = 0; i <= bumps; i++) {
      const x = CX - HALF + (IW * i) / bumps;
      top.push([x, this.surfaceYAt(x) + 4]);
    }
    // 顶面用二次贝塞尔穿点,得到柔和起伏而非折线
    g.moveTo(top[0][0], top[0][1]);
    for (let i = 1; i < top.length; i++) {
      const [px, py] = top[i - 1], [x, y] = top[i];
      g.quadraticCurveTo((px + x) / 2, (py + y) / 2 - 3, x, y);
    }
    return top;
  }

  private drawBody(sil: number, rim: number) {
    const g = this.body; g.clear();
    const gy = GROUND_Y;

    // ── 岛体:顶面 + 倒锥岩体 + 3 条垂落岩须 ──
    // 【修复】原实现整条下缘全用 lineTo,产出一个廉价的折线三角(而远岛 coneAt 反而用了
    // bezierCurveTo)。§B3 明令"外轮廓一律用圆角/贝塞尔,禁止直角锯齿",这里改为全贝塞尔。
    const top = this.tracedTop(g);
    const tip = (x0: number, x1: number, depth: number) => {
      // 一条垂落岩须:两侧内收的曲线汇于一个圆钝的尖
      const mx = (x0 + x1) / 2;
      g.bezierCurveTo(x0 + (mx - x0) * 0.55, gy + depth * 0.42, mx - (mx - x0) * 0.30, gy + depth * 0.80, mx, gy + depth);
      g.bezierCurveTo(mx + (x1 - mx) * 0.30, gy + depth * 0.80, x1 - (x1 - mx) * 0.55, gy + depth * 0.42, x1, gy + 118);
    };
    // 右肩下沉
    g.bezierCurveTo(CX + HALF * 0.99, gy + 26, CX + HALF * 0.86, gy + 74, CX + HALF * 0.66, gy + 118);
    tip(CX + HALF * 0.66, CX + HALF * 0.30, 250);   // 右须
    tip(CX + HALF * 0.30, CX - HALF * 0.16, 330);   // 中须(最长)
    tip(CX - HALF * 0.16, CX - HALF * 0.66, 236);   // 左须
    // 左肩回升
    g.bezierCurveTo(CX - HALF * 0.86, gy + 74, CX - HALF * 0.99, gy + 26, top[0][0], top[0][1]);
    g.closePath();
    g.fill(sil);

    // ── 顶部 rim 光边 ──
    // 【修复】原实现 fill() 消费掉路径后又复用同一路径 stroke,渲染成断续虚线。
    // 这里重新描一遍上缘再 stroke,得到连续光边。
    this.tracedTop(g);
    g.stroke({ width: 2, color: rim, alpha: 0.85 });

    // 两级台地 rim(左村落 / 右石碑广场)
    g.moveTo(CX - 380, this.surfaceYAt(CX - 380) + 2).lineTo(CX - 120, this.surfaceYAt(CX - 120) + 2)
      .stroke({ width: 1, color: rim, alpha: 0.4 });
    g.moveTo(CX + 120, this.surfaceYAt(CX + 120) + 2).lineTo(CX + 380, this.surfaceYAt(CX + 380) + 2)
      .stroke({ width: 1, color: rim, alpha: 0.4 });
  }

  private drawTrees(sil: number) {
    const g = this.trees; g.clear();
    const grow = this.treeGrow;
    if (grow < 0.02) return;   // 混沌/黎明纪:树属于"生命",还未到来
    // 风力系数 0.0004→0.0011:起风神迹 wind=±140 时树冠明显倾摆——
    // "风修饰一切"必须真的看得见,而不是只吹雨丝。
    const sway = 0.03 * Math.sin((this.t / 6) * Math.PI * 2) + windSrc.wind.x * 0.0011;
    for (const tr of this.treeList) {
      const s = sway + 0.006 * Math.sin(this.t * 0.7 + tr.ph);
      const h = tr.h * grow;
      // 树干:锥形贝塞尔(原来是一根等宽 4px 直线,配上三个正圆 = 棒棒糖)
      const tipX = tr.x + s * h, tipY = tr.baseY - h * 0.72;
      g.moveTo(tr.x - 3.5 * grow, tr.baseY);
      g.quadraticCurveTo(tr.x - 1.6 + s * h * 0.4, tr.baseY - h * 0.4, tipX - 1.4, tipY);
      g.lineTo(tipX + 1.4, tipY);
      g.quadraticCurveTo(tr.x + 1.6 + s * h * 0.4, tr.baseY - h * 0.4, tr.x + 3.5 * grow, tr.baseY);
      g.closePath();
      // 三团圆冠:与树干入同一路径,一次性 fill —— 合并成单一剪影而非三个独立正圆
      for (const c of tr.crowns) {
        const off = s * Math.abs(c.dy) * grow;
        g.ellipse(tr.x + c.dx * grow + off, tr.baseY + c.dy * grow, c.r * 1.08 * grow, c.r * 0.86 * grow);
      }
      g.fill(sil);
    }
  }
}
